import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentPreviewModal = ({ isOpen, onClose, document }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 5; // Mock total pages

  if (!isOpen || !document) return null;

  const getDocumentTypeIcon = (type) => {
    const typeIcons = {
      'deed': 'FileText',
      'contract': 'FileSignature',
      'assessment': 'ClipboardCheck',
      'certificate': 'Award',
      'report': 'BarChart3',
      'other': 'File'
    };
    return typeIcons?.[type] || 'File';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      'signed': { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      'rejected': { color: 'bg-error text-error-foreground', icon: 'XCircle' },
      'expired': { color: 'bg-muted text-muted-foreground', icon: 'AlertTriangle' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.['pending'];
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={getDocumentTypeIcon(document?.type)} size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{document?.filename}</h2>
              <div className="flex items-center space-x-4 mt-1">
                <p className="text-sm text-muted-foreground">
                  Transaction #{document?.transactionId}
                </p>
                <span className="text-muted-foreground">•</span>
                <p className="text-sm text-muted-foreground">
                  {document?.size}
                </p>
                <span className="text-muted-foreground">•</span>
                {getStatusBadge(document?.signatureStatus)}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
            >
              Download
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Document Info Bar */}
        <div className="flex items-center justify-between p-4 bg-muted/30 border-b border-border">
          <div className="flex items-center space-x-6 text-sm">
            <div>
              <span className="text-muted-foreground">Uploaded:</span>
              <span className="ml-2 font-medium text-foreground">
                {new Date(document.uploadDate)?.toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">By:</span>
              <span className="ml-2 font-medium text-foreground">{document?.uploadedBy}</span>
            </div>
            <div className="flex items-center space-x-2">
              {document?.blockchainVerified ? (
                <>
                  <Icon name="Shield" size={14} className="text-success" />
                  <span className="text-success font-medium">Blockchain Verified</span>
                </>
              ) : (
                <>
                  <Icon name="Clock" size={14} className="text-warning" />
                  <span className="text-warning font-medium">Verification Pending</span>
                </>
              )}
            </div>
          </div>
          
          {/* Page Navigation */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <Icon name="ChevronLeft" size={16} />
              </Button>
              
              <span className="text-sm font-medium text-foreground">
                Page {currentPage} of {totalPages}
              </span>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
              >
                <Icon name="ZoomOut" size={16} />
              </Button>
              <span className="text-sm text-muted-foreground">100%</span>
              <Button
                variant="ghost"
                size="icon"
              >
                <Icon name="ZoomIn" size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Document Viewer */}
        <div className="flex-1 p-6 overflow-auto bg-muted/10">
          <div className="max-w-4xl mx-auto">
            {/* Mock Document Content */}
            <div className="bg-white rounded-lg shadow-soft border border-border min-h-[800px] p-8">
              <div className="space-y-6">
                <div className="text-center border-b border-gray-200 pb-6">
                  <h1 className="text-2xl font-bold text-gray-900">PROPERTY DEED</h1>
                  <p className="text-gray-600 mt-2">State of California, County of Los Angeles</p>
                </div>
                
                <div className="space-y-4">
                  <p className="text-gray-800 leading-relaxed">
                    This deed is made this 15th day of September, 2025, between John Smith and Jane Smith, 
                    husband and wife (Grantors), and Michael Johnson (Grantee).
                  </p>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Property Description:</h3>
                    <p className="text-gray-700">
                      The real property situated in the City of Los Angeles, County of Los Angeles, 
                      State of California, described as follows: Lot 15, Block 3, Sunset Heights 
                      Subdivision, as per map recorded in Book 45, Page 67 of Maps, in the office 
                      of the County Recorder of said County.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Grantor Signatures:</h4>
                      <div className="space-y-2">
                        <div className="h-12 border-b border-gray-300 flex items-end">
                          <span className="text-sm text-gray-600">John Smith</span>
                        </div>
                        <div className="h-12 border-b border-gray-300 flex items-end">
                          <span className="text-sm text-gray-600">Jane Smith</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Grantee Signature:</h4>
                      <div className="h-12 border-b border-gray-300 flex items-end">
                        <span className="text-sm text-gray-600">Michael Johnson</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">
                        Blockchain Verification Hash:
                      </span>
                    </div>
                    <p className="text-xs font-mono text-blue-700 mt-1">
                      0x1a2b3c4d5e6f7890abcdef1234567890abcdef1234567890abcdef1234567890
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              iconName="History"
              iconPosition="left"
            >
              Version History
            </Button>
            <Button
              variant="outline"
              iconName="Share"
              iconPosition="left"
            >
              Share Document
            </Button>
          </div>
          
          <div className="flex items-center space-x-3">
            {document?.signatureStatus === 'pending' && (
              <Button
                variant="default"
                iconName="PenTool"
                iconPosition="left"
              >
                Request Signature
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;